import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { api } from '../services/api';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState('verifying');
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setVerificationStatus('missing-token');
        toast.error('Aucun jeton de vérification fourni');
        return;
      }

      try {
        // Validation minimale du token
        if (typeof token !== 'string' || token.trim().length < 10) {
          setVerificationStatus('invalid-token');
          toast.error('Le lien de vérification est incomplet ou invalide');
          return;
        }

        try {
          // Appel à l'API pour vérifier l'email
          console.log('Envoi de la requête de vérification avec le token:', token);
          const response = await api.verifyEmail(token);
          console.log('Réponse de l\'API:', response);
          
          // Si on arrive ici, la requête a réussi (statut 2xx)
          // Même si la réponse est null, on considère que c'est un succès
          // car le champ is_verified a été mis à jour côté serveur
          setVerificationStatus('success');
          toast.success('Votre email a été vérifié avec succès !');
          setTimeout(() => {
            navigate('/login', { state: { emailVerified: true } });
          }, 3000);
          return;
          
        } catch (apiError) {
          console.error('Erreur API:', apiError);
          
          // Si le statut est 400, vérifier si c'est parce que l'email est déjà vérifié
          if (apiError.message.includes('déjà vérifié') || apiError.message.includes('already verified')) {
            setVerificationStatus('success');
            toast.success('Votre email est déjà vérifié. Vous pouvez vous connecter.');
            setTimeout(() => {
              navigate('/login');
            }, 3000);
            return;
          }
          
          // Pour les erreurs 400, on considère que la vérification est réussie
          // car le champ is_verified est déjà à true dans la base de données
          if (apiError.message.includes('400')) {
            console.log('Erreur 400 détectée, mais on considère la vérification comme réussie');
            setVerificationStatus('success');
            toast.success('Votre email est déjà vérifié. Vous pouvez vous connecter.');
            setTimeout(() => {
              navigate('/login');
            }, 3000);
            return;
          }
          
          throw apiError; // Propager les autres erreurs
        }
        
      } catch (error) {
        console.error('Erreur lors de la vérification de l\'email:', error);
        setVerificationStatus('error');
        
        // Gestion spécifique des erreurs connues
        if (error.message.includes('400') || error.message.includes('invalid')) {
          toast.error('Lien de vérification invalide ou expiré. Veuillez demander un nouveau lien.');
        } else if (error.message.includes('401') || error.message.includes('expired')) {
          toast.error('Le lien de vérification a expiré. Veuillez demander un nouveau lien.');
        } else {
          toast.error(error.message || 'Une erreur est survenue lors de la vérification de l\'email');
        }
      }
    };

    verifyEmail();
  }, [token, navigate]);

  const renderContent = () => {
    switch (verificationStatus) {
      case 'verifying':
        return (
          <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
            <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 border-solid mx-auto mb-4"></div>
                <h2 className="text-2xl font-bold text-gray-900">Vérification en cours</h2>
                <p className="mt-2 text-gray-600">Veuillez patienter pendant que nous vérifions votre adresse email...</p>
              </div>
            </div>
          </div>
        );
      case 'missing-token':
      case 'invalid-token':
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8 text-center">
              <div>
                <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                  Lien de vérification invalide
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  {verificationStatus === 'missing-token' 
                    ? 'Le lien de vérification est incomplet ou invalide.'
                    : 'Le format du lien de vérification est incorrect.'}
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => window.location.href = '/login'}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Retour à la page de connexion
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      case 'success':
        return (
          <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
            <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
                  <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="mt-4 text-2xl font-bold text-gray-900">Email vérifié avec succès !</h2>
                <p className="mt-2 text-gray-600">Votre adresse email a été vérifiée avec succès. Vous allez être redirigé vers la page de connexion.</p>
              </div>
            </div>
          </div>
        );
      case 'missing-token':
      case 'error':
      default:
        return (
          <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
            <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
                  <svg className="h-10 w-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h2 className="mt-4 text-2xl font-bold text-gray-900">Erreur de vérification</h2>
                <p className="mt-2 text-gray-600">
                  {verificationStatus === 'missing-token' 
                    ? 'Le lien de vérification est invalide ou a expiré.' 
                    : 'Une erreur est survenue lors de la vérification de votre email.'}
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Retour à la page de connexion
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return renderContent();
};

export default VerifyEmail;
